import { formatInTimeZone } from 'date-fns-tz';

export interface TimezoneEntry {
  value: string;
  label: string;
}

export const TIMEZONES: TimezoneEntry[] = [
  // Americas
  { value: 'America/New_York', label: 'US - New York' },
  { value: 'America/Chicago', label: 'US - Chicago' },
  { value: 'America/Denver', label: 'US - Denver' },
  { value: 'America/Los_Angeles', label: 'US - Los Angeles' },
  { value: 'America/Anchorage', label: 'US - Anchorage' },
  { value: 'Pacific/Honolulu', label: 'US - Hawaii' },
  { value: 'America/Phoenix', label: 'US - Arizona' },
  { value: 'America/Toronto', label: 'Canada - Toronto' },
  { value: 'America/Vancouver', label: 'Canada - Vancouver' },
  { value: 'America/Mexico_City', label: 'Mexico - Mexico City' },
  { value: 'America/Sao_Paulo', label: 'Brazil - São Paulo' },
  { value: 'America/Buenos_Aires', label: 'Argentina - Buenos Aires' },
  { value: 'America/Santiago', label: 'Chile - Santiago' },
  { value: 'America/Lima', label: 'Peru - Lima' },
  { value: 'America/Bogota', label: 'Colombia - Bogotá' },

  // Europe
  { value: 'Europe/London', label: 'UK - London' },
  { value: 'Europe/Dublin', label: 'Ireland - Dublin' },
  { value: 'Europe/Paris', label: 'France - Paris' },
  { value: 'Europe/Berlin', label: 'Germany - Berlin' },
  { value: 'Europe/Rome', label: 'Italy - Rome' },
  { value: 'Europe/Madrid', label: 'Spain - Madrid' },
  { value: 'Europe/Amsterdam', label: 'Netherlands - Amsterdam' },
  { value: 'Europe/Brussels', label: 'Belgium - Brussels' },
  { value: 'Europe/Vienna', label: 'Austria - Vienna' },
  { value: 'Europe/Stockholm', label: 'Sweden - Stockholm' },
  { value: 'Europe/Copenhagen', label: 'Denmark - Copenhagen' },
  { value: 'Europe/Oslo', label: 'Norway - Oslo' },
  { value: 'Europe/Helsinki', label: 'Finland - Helsinki' },
  { value: 'Europe/Athens', label: 'Greece - Athens' },
  { value: 'Europe/Istanbul', label: 'Turkey - Istanbul' },
  { value: 'Europe/Moscow', label: 'Russia - Moscow' },
  { value: 'Europe/Warsaw', label: 'Poland - Warsaw' },
  { value: 'Europe/Prague', label: 'Czech Republic - Prague' },
  { value: 'Europe/Zurich', label: 'Switzerland - Zurich' },

  // Asia
  { value: 'Asia/Dubai', label: 'UAE - Dubai' },
  { value: 'Asia/Kolkata', label: 'India - Kolkata' },
  { value: 'Asia/Karachi', label: 'Pakistan - Karachi' },
  { value: 'Asia/Dhaka', label: 'Bangladesh - Dhaka' },
  { value: 'Asia/Bangkok', label: 'Thailand - Bangkok' },
  { value: 'Asia/Singapore', label: 'Singapore' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong' },
  { value: 'Asia/Shanghai', label: 'China - Shanghai' },
  { value: 'Asia/Tokyo', label: 'Japan - Tokyo' },
  { value: 'Asia/Seoul', label: 'South Korea - Seoul' },
  { value: 'Asia/Manila', label: 'Philippines - Manila' },
  { value: 'Asia/Jakarta', label: 'Indonesia - Jakarta' },
  { value: 'Asia/Kuala_Lumpur', label: 'Malaysia - Kuala Lumpur' },
  { value: 'Asia/Taipei', label: 'Taiwan - Taipei' },
  { value: 'Asia/Jerusalem', label: 'Israel - Jerusalem' },
  { value: 'Asia/Riyadh', label: 'Saudi Arabia - Riyadh' },

  // Pacific / Oceania
  { value: 'Australia/Sydney', label: 'Australia - Sydney' },
  { value: 'Australia/Melbourne', label: 'Australia - Melbourne' },
  { value: 'Australia/Brisbane', label: 'Australia - Brisbane' },
  { value: 'Australia/Perth', label: 'Australia - Perth' },
  { value: 'Australia/Adelaide', label: 'Australia - Adelaide' },
  { value: 'Pacific/Auckland', label: 'New Zealand - Auckland' },
  { value: 'Pacific/Fiji', label: 'Fiji' },
  { value: 'Pacific/Guam', label: 'Guam' },

  // Africa
  { value: 'Africa/Cairo', label: 'Egypt - Cairo' },
  { value: 'Africa/Johannesburg', label: 'South Africa - Johannesburg' },
  { value: 'Africa/Lagos', label: 'Nigeria - Lagos' },
  { value: 'Africa/Nairobi', label: 'Kenya - Nairobi' },
  { value: 'Africa/Casablanca', label: 'Morocco - Casablanca' },
];

/**
 * Get the current UTC offset for a timezone, DST-aware.
 * Returns e.g. "-04:00" during EDT, "-05:00" during EST.
 */
export function getDynamicOffset(timezone: string, date: Date = new Date()): string {
  try {
    return formatInTimeZone(date, timezone, 'xxx');
  } catch {
    return '+00:00';
  }
}

/**
 * Get the short timezone abbreviation (e.g., "EDT", "PST"), DST-aware.
 */
export function getTimezoneAbbreviation(timezone: string, date: Date = new Date()): string {
  try {
    return formatInTimeZone(date, timezone, 'zzz');
  } catch {
    return 'UTC';
  }
}

/**
 * Get timezones with their current dynamic offsets for display purposes.
 */
export function getTimezonesWithOffsets(date: Date = new Date()) {
  return TIMEZONES.map(tz => ({
    ...tz,
    offset: getDynamicOffset(tz.value, date),
    abbreviation: getTimezoneAbbreviation(tz.value, date),
  }));
}
